import React, { forwardRef, useRef, useState, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  DialogHandle,
  Dialog,
  useEventCallback,
  Form,
  FormHandle,
  FormTextArea,
  SnackbarVariant,
  SnackbarContext,
  DetailContext,
  FormSelect,
} from '@eas/common-web';
import { Comment, CommentDialogProps } from './tickets-types';
import Grid from '@material-ui/core/Grid';
import { useStyles } from './tickets-styles';
import { comment } from './tickets-api';
import { useTicketCommentInternal } from '../operator-api';
import { TicketDialogLeftPanel } from './ticket-dialog-left-panel';

export const CommentDialog = forwardRef<DialogHandle, CommentDialogProps>(
  function RequestAmendDialog({ id, ticket }, dialogRef) {
    const classes = useStyles();
    const intl = useIntl();
    const { showSnackbar } = useContext(SnackbarContext);
    const { refreshAll } = useContext(DetailContext);

    const ref = useRef<FormHandle<Comment>>(null);
    const [loading, setLoading] = useState(false);

    const onConfirm = useEventCallback(() => {
      ref.current?.submitForm();
    });

    const handleSubmit = useEventCallback(async (values: Comment) => {
      try {
        setLoading(true);

        values.internal = values.internalText === 'internal';

        await comment(id!, values).none();

        const message = intl.formatMessage({
          id: 'KS_O_TICKETS_COMMNENT_DIALOG_MSG_SUCCESS',
          defaultMessage: 'Komentář byl odeslán',
        });

        showSnackbar(message, SnackbarVariant.SUCCESS);
        setLoading(false);

        refreshAll();
      } catch (err) {
        if (err.name !== 'AbortError') {
          const message = intl.formatMessage(
            {
              id: 'KS_O_TICKETS_COMMNENT_DIALOG_MSG_ERROR',
              defaultMessage: 'Chyba komentáře: {detail}',
            },
            { detail: err.message }
          );

          showSnackbar(message, SnackbarVariant.ERROR);
          setLoading(false);

          throw err;
        }
      }
    });

    const internals = useTicketCommentInternal();

    return (
      <Dialog
        disableBackdrop
        ref={dialogRef}
        loading={loading}
        title={
          <FormattedMessage
            id="KS_O_TICKETS_COMMNENT_DIALOG_TITLE"
            defaultMessage="Přidat komentář"
          />
        }
        onConfirm={onConfirm}
      >
        {() => (
          <Grid container className={classes.actionDialogContainerWrapper}>
            <Grid
              item
              xs={4}
              sm={4}
              className={classes.actionDialogQuestionWrapper}
            >
              <TicketDialogLeftPanel ticket={ticket} />
            </Grid>
            <Grid
              item
              xs={8}
              sm={8}
              className={classes.actionDialogAnswerWrapper}
            >
              <Form<Comment>
                editing={!loading}
                initialValues={{
                  text: '',
                  internal: true,
                  internalText: 'internal',
                }}
                onSubmit={handleSubmit}
                ref={ref}
              >
                <Grid container className={classes.actionDialogWrapper}>
                  <Grid item xs={12} sm={12}>
                    <FormTextArea
                      maxRows={25}
                      required
                      name="text"
                      label={
                        <FormattedMessage
                          id="KS_O_TICKETS_COMMNENT_DIALOG_FIELD_LABEL_TEXT"
                          defaultMessage="Komentář"
                        />
                      }
                      helpLabel={intl.formatMessage({
                        id:
                          'KS_O_TICKETS_COMMNENT_DIALOG_FIELD_LABEL_HELP_TEXT',
                        defaultMessage: ' ',
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <FormSelect
                      required
                      name="internalText"
                      label={
                        <FormattedMessage
                          id="KS_O_TICKETS_COMMNENT_DIALOG_FIELD_LABEL_INTERNAL"
                          defaultMessage="Viditelnost"
                        />
                      }
                      helpLabel={intl.formatMessage({
                        id:
                          'KS_O_TICKETS_COMMNENT_DIALOG_FIELD_LABEL_HELP_INTERNAL',
                        defaultMessage: ' ',
                      })}
                      source={internals}
                      tooltipMapper={(o) => o.name}
                      valueIsId={true}
                    />
                  </Grid>
                </Grid>
              </Form>
            </Grid>
          </Grid>
        )}
      </Dialog>
    );
  }
);
